import { Box, ButtonBase } from '@mui/material'
import { useRef, type KeyboardEvent } from 'react'

import { NAVY } from './styles'

interface SegmentedRadioProps<T extends string> {
  /** The group's accessible name. */
  label: string
  /** Id of visible text that explains the choice, read after the name. */
  describedBy?: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}

const NEXT_KEYS = ['ArrowRight', 'ArrowDown']
const PREV_KEYS = ['ArrowLeft', 'ArrowUp']

/**
 * Two or more joined buttons where exactly one is chosen. Screen readers hear
 * a radio group ("Include, selected, 1 of 2"); the group is one Tab stop and
 * the arrow keys move the choice, as for native radio buttons.
 */
export default function SegmentedRadio<T extends string>({
  label,
  describedBy,
  value,
  options,
  onChange,
}: SegmentedRadioProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])

  function onKeyDown(event: KeyboardEvent, index: number) {
    let next = index
    if (NEXT_KEYS.includes(event.key)) next = (index + 1) % options.length
    else if (PREV_KEYS.includes(event.key)) next = (index - 1 + options.length) % options.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = options.length - 1
    else return
    event.preventDefault()
    onChange(options[next].value)
    buttons.current[next]?.focus()
  }

  return (
    <Box
      role="radiogroup"
      aria-label={label}
      aria-describedby={describedBy}
      sx={{ display: 'inline-flex', flexShrink: 0 }}
    >
      {options.map((option, index) => {
        const checked = option.value === value
        return (
          <ButtonBase
            key={option.value}
            ref={(el) => {
              buttons.current[index] = el
            }}
            disableRipple
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            sx={{
              minHeight: 28,
              px: 1.5,
              fontSize: '13px',
              fontFamily: 'inherit',
              color: checked ? '#fff' : NAVY,
              bgcolor: checked ? NAVY : '#fff',
              border: `1px solid ${NAVY}`,
              ...(index > 0 && { ml: '-1px' }),
              borderRadius:
                index === 0
                  ? '4px 0 0 4px'
                  : index === options.length - 1
                    ? '0 4px 4px 0'
                    : 0,
              '&:hover': { bgcolor: checked ? NAVY : 'rgba(28, 48, 69, 0.04)' },
              '&.Mui-focusVisible': {
                outline: `2px solid ${NAVY}`,
                outlineOffset: 2,
                zIndex: 1,
              },
            }}
          >
            {option.label}
          </ButtonBase>
        )
      })}
    </Box>
  )
}
